import { computed, ref, watch, readonly, onBeforeUnmount, type Ref } from 'vue';
import { useSdk } from '~/sdk';
import type { PasswordRequirement, PasswordStrength } from '~/utils/passwordValidation';

// Extended password strength interface for API validation
export interface ApiPasswordStrength extends PasswordStrength {
  description?: string;
  crack_time_display?: string;
  feedback?: {
    warning: string;
    suggestions: string[];
  };
}

// Additional types for API validation
export interface PasswordPolicy {
  min_length: number;
  max_length: number;
  minimum_score: number;
  score_descriptions: {
    '0': string;
    '1': string;
    '2': string;
    '3': string;
    '4': string;
  };
}

export interface PasswordValidationResult {
  is_valid: boolean;
  length: number;
  validations: {
    length_valid: boolean;
    not_too_short: boolean;
    not_too_long: boolean;
    acceptable_score: boolean;
  };
  requirements: {
    min_length: number;
    max_length: number;
    minimum_score: number;
  };
  score: {
    score: number;
    description: string;
    crack_time_display: string;
    feedback: {
      warning: string;
      suggestions: string[];
    };
  };
  errors: string[];
  suggestions: string[];
}

export function usePasswordValidation(password: Ref<string>) {
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;
  
  // Reactive state
  const validationResult = ref<PasswordValidationResult | null>(null);
  const passwordPolicy = ref<PasswordPolicy | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const timeoutId = ref<NodeJS.Timeout | null>(null);

  // Fetch password policy on initialization
  const fetchPasswordPolicy = async () => {
    try {
      const response = await useSdk().commerce.getPasswordPolicy();
      if (response.success && response.psdata) {
        passwordPolicy.value = response.psdata;
      }
    } catch (err) {
      console.warn('Failed to fetch password policy:', err);
      // Fallback to default policy
      passwordPolicy.value = {
        min_length: 8,
        max_length: 72,
        minimum_score: 3,
        score_descriptions: {
          '0': t('passwordValidation.strength.veryWeak') || 'Very Weak',
          '1': t('passwordValidation.strength.weak') || 'Weak',
          '2': t('passwordValidation.strength.fair') || 'Fair',
          '3': t('passwordValidation.strength.good') || 'Good',
          '4': t('passwordValidation.strength.strong') || 'Strong',
        }
      };
    }
  };

  // Validate password with API
  const validatePassword = async (passwordValue: string) => {
    if (!passwordValue) {
      validationResult.value = null;
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const response = await useSdk().commerce.validatePassword({ password: passwordValue });
      if (response.success && response.psdata) {
        validationResult.value = response.psdata;
      } else {
        error.value = response.message || 'Validation failed';
        // Fallback to client-side validation
        createFallbackValidation(passwordValue);
      }
    } catch (err) {
      error.value = 'Network error';
      // Fallback to client-side validation
      createFallbackValidation(passwordValue);
    } finally {
      isLoading.value = false;
    }
  };

  // Fallback validation when API is unavailable
  const createFallbackValidation = (passwordValue: string) => {
    const policy = passwordPolicy.value;
    if (!policy) return;

    const length = passwordValue.length;
    const hasLower = /[a-z]/.test(passwordValue);
    const hasUpper = /[A-Z]/.test(passwordValue);
    const hasNumbers = /[0-9]/.test(passwordValue);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue);

    // Simple scoring based on criteria met
    let score = 0;
    if (length >= policy.min_length) score++;
    if (hasLower) score++;
    if (hasUpper) score++;
    if (hasNumbers) score++;
    if (hasSpecial) score++;

    const isValid = length >= policy.min_length && 
                   length <= policy.max_length && 
                   score >= policy.minimum_score;

    const errors: string[] = [];
    if (length < policy.min_length) {
      errors.push(t('passwordValidation.errors.tooShort', { min: policy.min_length }));
    }
    if (length > policy.max_length) {
      errors.push(t('passwordValidation.errors.tooLong', { max: policy.max_length }));
    }
    if (score < policy.minimum_score) {
      errors.push(t('passwordValidation.errors.weakScore'));
    }

    validationResult.value = {
      is_valid: isValid,
      length,
      validations: {
        length_valid: length >= policy.min_length && length <= policy.max_length,
        not_too_short: length >= policy.min_length,
        not_too_long: length <= policy.max_length,
        acceptable_score: score >= policy.minimum_score,
      },
      requirements: {
        min_length: policy.min_length,
        max_length: policy.max_length,
        minimum_score: policy.minimum_score,
      },
      score: {
        score: Math.min(score, 4),
        description: policy.score_descriptions[Math.min(score, 4).toString() as keyof typeof policy.score_descriptions] || 'Unknown',
        crack_time_display: 'Unknown',
        feedback: {
          warning: '',
          suggestions: [],
        },
      },
      errors,
      suggestions: [],
    };
  };

  // Watch password changes with debouncing
  watch(password, (newPassword) => {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
    }
    timeoutId.value = setTimeout(() => {
      validatePassword(newPassword);
    }, 300); // 300ms debounce
  }, { immediate: true });

  // Computed properties for easier access
  const requirements = computed((): PasswordRequirement[] => {
    const result = validationResult.value;
    const policy = passwordPolicy.value;
    
    if (!result || !policy) {
      // Return basic requirements if no data
      return [
        { key: 'length', label: t('passwordValidation.requirements.length', { min: 8 }), satisfied: false, test: () => false },
        { key: 'lowercase', label: t('passwordValidation.requirements.lowercase'), satisfied: false, test: () => false },
        { key: 'uppercase', label: t('passwordValidation.requirements.uppercase'), satisfied: false, test: () => false },
        { key: 'numbers', label: t('passwordValidation.requirements.numbers'), satisfied: false, test: () => false },
        { key: 'special', label: t('passwordValidation.requirements.special'), satisfied: false, test: () => false },
      ];
    }

    const passwordValue = password.value;
    return [
      {
        key: 'length',
        label: t('passwordValidation.requirements.length', { min: policy.min_length }),
        satisfied: result.validations.length_valid,
        test: (password: string) => password.length >= policy.min_length,
      },
      {
        key: 'lowercase',
        label: t('passwordValidation.requirements.lowercase'),
        satisfied: /[a-z]/.test(passwordValue),
        test: (password: string) => /[a-z]/.test(password),
      },
      {
        key: 'uppercase',
        label: t('passwordValidation.requirements.uppercase'),
        satisfied: /[A-Z]/.test(passwordValue),
        test: (password: string) => /[A-Z]/.test(password),
      },
      {
        key: 'numbers',
        label: t('passwordValidation.requirements.numbers'),
        satisfied: /[0-9]/.test(passwordValue),
        test: (password: string) => /[0-9]/.test(password),
      },
      {
        key: 'special',
        label: t('passwordValidation.requirements.special'),
        satisfied: /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue),
        test: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
      },
    ];
  });

  const strength = computed((): ApiPasswordStrength => {
    const result = validationResult.value;
    
    if (!result || !password.value) {
      return {
        score: 0,
        label: t('passwordValidation.strength.veryWeak'),
        color: 'bg-red-500',
        percentage: 0,
      };
    }

    const score = result.score.score;
    let color = 'bg-red-500';
    let percentage = Math.round((score / 4) * 100);

    switch (score) {
      case 0:
        color = 'bg-red-500';
        break;
      case 1:
        color = 'bg-red-400';
        break;
      case 2:
        color = 'bg-orange-500';
        break;
      case 3:
        color = 'bg-yellow-500';
        break;
      case 4:
        color = 'bg-green-500';
        break;
    }

    return {
      score,
      label: result.score.description,
      color,
      percentage,
      description: result.score.description,
      crack_time_display: result.score.crack_time_display,
      feedback: result.score.feedback,
    };
  });

  const isValid = computed((): boolean => {
    return validationResult.value?.is_valid ?? false;
  });

  const validationMessage = computed((): string | null => {
    const result = validationResult.value;
    if (!result || result.is_valid) return null;
    return result.errors.join(', ') || null;
  });

  const unsatisfiedRequirements = computed((): PasswordRequirement[] => {
    return requirements.value.filter(req => !req.satisfied);
  });

  const satisfiedRequirements = computed((): PasswordRequirement[] => {
    return requirements.value.filter(req => req.satisfied);
  });

  // Initialize policy on creation
  fetchPasswordPolicy();

  // Clean up the timeout on component unmount
  onBeforeUnmount(() => {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
  });

  return {
    // Data
    validationResult: readonly(validationResult),
    passwordPolicy: readonly(passwordPolicy),
    
    // State
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // Computed
    requirements,
    strength,
    isValid,
    validationMessage,
    unsatisfiedRequirements,
    satisfiedRequirements,
    
    // Methods
    validatePassword,
    fetchPasswordPolicy,
  };
}
